import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BsCheckCircle, BsXCircle } from 'react-icons/bs'

import { useConfig } from '../../contexts/ConfigContext'
import { useTheme } from '../../contexts/ThemeContext'
import { Button } from '../Shared/Buttons'
import ApiService from '../../service/ApiService'
import setting from '../../setting.json'

import {
  PayPage,
  LangBar,
  LangBtn,
  BillCard,
  BillHeader,
  LogoCircle,
  LogoPlaceholder,
  BillTitle,
  StatusBanner,
  BillBody,
  DetailRow,
  DetailTitle,
  DetailVal,
  StatusVal,
  AmountBlock,
  MethodsSection,
  MethodsTitle,
  MethodsList,
  MethodItem,
  MethodIcons,
  MethodName,
  ApplePayWrap,
  BillFooter,
  CompanyCard,
  CompanyName,
  CompanyLine,
  ErrorCard
} from './styles'

const SUPPORT_EMAIL = 'support@althogsweet.com'
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000
const SUCCESS_CODES = ['CAPTURED', 'ACCEPT', 'SUCCESS']

/**
 * Hesabe paymentType for pay-link checkout (POST /pay/checkout)
 * 1 = KNET
 * 2 = credit card
 * 9 = MPGS Apple Pay
 * 11 = KNET Debit Apple Pay
 * 12 = KNET Credit Apple Pay
 * 13 = KNET Apple Pay International (credit + debit) — default
 * 0 = indirect hosted picker (Google Pay / fallback)
 */
const METHOD_TO_APP_PAYMENT_TYPE = {
  knet: 1,
  credit: 2,
  applepay: 13,
  googlepay: 0
}

const MERCHANT_DOMAIN = 'althogsweet.com'

/**
 * Credit card: full-page Hesabe /payment?data=TOKEN (not iframe).
 * MPGS/NBK 3DS refuses nested frames (X-Frame-Options: sameorigin).
 *
 * Apple Pay Direct uses Apple’s SDK + /applepayment (on-site sheet).
 */
const HESABE_APPLE_PAY_SDK = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js'
/**
 * Page / merchant domain for Apple Pay validation (must match the live site).
 * Hesabe docs: variable5 = merchant domain; Apple session domainName must match
 * the page origin (althogsweet.com), NOT api.hesabe.com.
 */
const getMerchantValidateDomain = () => {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname.replace(/^www\./, '')
  }
  return MERCHANT_DOMAIN
}

/** Hesabe PSP host — fallback only if merchant domain probe fails. */
const hesabeApplePspHost = (baseUrl, environment) => {
  const base = String(baseUrl || '')
  const env = String(environment || '')
  if (base.includes('sandbox.hesabe.com') || env === 'staging' || env === 'sandbox') {
    return 'sandbox.hesabe.com'
  }
  return 'api.hesabe.com'
}

/** Prefer merchant domain first; keep Hesabe host as last-resort probe. */
const buildAppleValidateDomains = (baseUrl, environment, extra = []) => {
  const merchant = getMerchantValidateDomain()
  const psp = hesabeApplePspHost(baseUrl, environment)
  return Array.from(new Set([
    merchant,
    MERCHANT_DOMAIN,
    ...((Array.isArray(extra) ? extra : [extra]).filter(Boolean)),
    psp
  ].filter(Boolean)))
}

const loadExternalScript = (src) => new Promise((resolve, reject) => {
  const existing = document.querySelector(`script[src="${src}"]`)
  if (existing) {
    if (existing.dataset.loaded === '1') {
      resolve()
      return
    }
    existing.addEventListener('load', () => resolve())
    existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)))
    return
  }
  const script = document.createElement('script')
  script.src = src
  script.async = true
  script.onload = () => {
    script.dataset.loaded = '1'
    resolve()
  }
  script.onerror = () => reject(new Error(`Failed to load ${src}`))
  document.body.appendChild(script)
})

const PAYMENT_METHODS = [
  { id: 'knet', labelKey: 'knet', imageIcons: ['knet'] },
  { id: 'credit', labelKey: 'credit card', imageIcons: ['visa', 'master'] },
  { id: 'applepay', labelKey: 'Apple Pay', imageIcons: ['applepay'] },
  { id: 'googlepay', labelKey: 'Google Pay', imageIcons: ['googlepay'] }
]

const formatAmount = (amount) => {
  const n = Number(amount)
  if (Number.isNaN(n)) return '0.000'
  return n.toFixed(3)
}

const formatPayDate = (details) => {
  if (!details) return '—'
  if (details.paidOn) return details.paidOn
  if (details.date && details.time) return `${details.date} ${details.time}`
  if (details.date) return details.date
  return '—'
}

const parseCreatedAt = (createdAt) => {
  if (!createdAt) return null
  // Backend: "2026-09-27 01:56:05" (Asia/Kuwait wall time)
  const normalized = String(createdAt).trim().replace(' ', 'T')
  const d = new Date(normalized)
  if (!Number.isNaN(d.getTime())) return d
  const fallback = new Date(String(createdAt).replace(/-/g, '/'))
  return Number.isNaN(fallback.getTime()) ? null : fallback
}

const isWithin24Hours = (createdAt) => {
  const created = parseCreatedAt(createdAt)
  if (!created) return false
  return Date.now() - created.getTime() <= TWENTY_FOUR_HOURS_MS
}

/** status null / empty → unpaid bill that can be paid (if still within 24h) */
const isStatusNull = (details) => {
  const s = details?.status
  return s === null || s === undefined || s === ''
}

const isPaymentSuccess = (details) => {
  if (!details || isStatusNull(details)) return false
  const code = String(details.resultCode || '').toUpperCase()
  if (SUCCESS_CODES.includes(code)) return true
  if (details.status === true || details.status === 1 || details.status === '1') return true
  return false
}

const isPaymentFailed = (details) => {
  if (!details || isStatusNull(details)) return false
  if (isPaymentSuccess(details)) return false
  const s = details.status
  if (s === false || s === 0 || s === '0') return true
  const code = String(details.resultCode || '').toUpperCase()
  if (code && !SUCCESS_CODES.includes(code)) return true
  if (details.message) return true
  return false
}

/** Pay-link callbacks come from settings; fallback to /transaction on this origin. */
const fallbackTransactionUrl = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/transaction`
  }
  return `${setting.server}/pay/response`
}

const getPayResponseUrl = (configs) => (
  (configs?.pay_response_url && String(configs.pay_response_url).trim())
  || fallbackTransactionUrl()
)

const getPayFailureUrl = (configs) => (
  (configs?.pay_failure_url && String(configs.pay_failure_url).trim())
  || fallbackTransactionUrl()
)

const isGatewayConfigReady = (configs) => Boolean(
  getPayResponseUrl(configs) && getPayFailureUrl(configs)
)

/**
 * Normalize /pay/checkout JSON.
 * Live API may only return { success, message, url } — extract token from url.
 * Deployed PaymentController also returns token/checkoutData/paymentApiUrl/environment.
 */
const parseCheckoutResult = (result) => {
  const url = (result?.url && String(result.url)) || ''
  let token = (result?.checkoutData || result?.token || '')
  if (typeof token !== 'string') token = ''
  token = token.trim()

  if (!token && url) {
    try {
      const parsed = new URL(url)
      token = (parsed.searchParams.get('data') || '').trim()
    } catch (e) {
      const idx = url.indexOf('data=')
      if (idx >= 0) {
        token = decodeURIComponent(url.slice(idx + 5).split('&')[0] || '').trim()
      }
    }
  }

  let baseUrl = (result?.paymentApiUrl && String(result.paymentApiUrl).replace(/\/$/, '')) || ''
  if (!baseUrl && url) {
    try {
      const parsed = new URL(url)
      baseUrl = `${parsed.protocol}//${parsed.host}`
    } catch (e) {
      baseUrl = ''
    }
  }
  if (!baseUrl) {
    baseUrl = (url && url.includes('api.hesabe.com'))
      ? 'https://api.hesabe.com'
      : (result?.environment === 'production' ? 'https://api.hesabe.com' : 'https://sandbox.hesabe.com')
  }

  const environment = result?.environment
    || (baseUrl.includes('sandbox') ? 'staging' : 'production')

  return { url, token, baseUrl, environment, webviewUrl: result?.webviewUrl || '' }
}

export const Pay = () => {
  const { t, i18n } = useTranslation()
  const { reference } = useParams()
  const history = useHistory()
  const [configState] = useConfig()
  const [theme, { update }] = useTheme()
  const [selectedMethod, setSelectedMethod] = useState(null)
  const [paying, setPaying] = useState(false)
  const [loadingPay, setLoadingPay] = useState(true)
  const [payError, setPayError] = useState('')
  const [payData, setPayData] = useState(null)
  const [applePayReady, setApplePayReady] = useState(false)
  const [applePaySession, setApplePaySession] = useState(null)
  const currentLng = (i18n.language || localStorage.getItem('i18nextLng') || 'en').startsWith('en')
    ? 'en'
    : 'ar'
  const isEnglish = currentLng === 'en'

  const logoSrc = configState?.configs?.logo || null
  const currency = configState?.configs?.currency || 'KWD'
  const companyName = isEnglish
    ? (configState?.configs?.appEnName || '')
    : (configState?.configs?.appArName || '')
  const whatsAppUrl = configState?.configs?.whatsAppUrl
  const configs = configState?.configs || {}

  const applyLanguage = (lng) => {
    i18n.changeLanguage(lng)
    localStorage.setItem('i18nextLng', lng)
    document.body.dir = lng === 'ar' ? 'rtl' : 'ltr'
    update({ ...theme, rtl: lng === 'ar' })
  }

  const toggleLanguage = () => {
    applyLanguage(currentLng === 'en' ? 'ar' : 'en')
  }

  // Pay-link page defaults to English
  useEffect(() => {
    applyLanguage('en')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let active = true
    const load = async () => {
      if (!reference) {
        setLoadingPay(false)
        setPayData(null)
        return
      }
      setLoadingPay(true)
      setPayError('')
      try {
        const result = await ApiService(`/pay/${reference}`, {}, 'get')
        if (!active) return
        if (result?.success && result?.order_details) {
          const d = result.order_details
          setPayData({
            reference: d.orderReferenceNumber || reference,
            orderNo: d.order_id || d.orderReferenceNumber || reference,
            amount: formatAmount(d.amount),
            date: formatPayDate(d),
            raw: d
          })
        } else {
          setPayData(null)
          const apiMsg = typeof result?.message === 'string' && result.message.trim()
            ? result.message.trim()
            : null
          // Laravel crashed (e.g. PaymentController missing) — don't mask as "missing bill"
          const looksLikeServerFault = apiMsg && (
            /does not exist|BindingResolution|Class .* not found|syntax error/i.test(apiMsg)
            || result?.exception
          )
          setPayError(
            looksLikeServerFault
              ? t('Payment gateway error')
              : (apiMsg || t('Missing payment reference'))
          )
        }
      } catch (err) {
        console.log(err, 'pay load error')
        if (active) {
          setPayData(null)
          setPayError(t('Payment gateway error'))
        }
      } finally {
        if (active) setLoadingPay(false)
      }
    }
    load()
    return () => { active = false }
  }, [reference, t])

  const billMode = useMemo(() => {
    if (!payData?.raw) return 'missing'
    const d = payData.raw
    const fresh = isWithin24Hours(d.created_at)
    if (!fresh) return 'expired'
    if (isStatusNull(d)) return 'payable'
    if (isPaymentSuccess(d)) return 'success'
    if (isPaymentFailed(d)) return 'failed'
    return 'expired'
  }, [payData])

  // Direct Apple Pay — Apple SDK sheet (no iframe). Validate via Hesabe /applepayment.
  useEffect(() => {
    if (!applePayReady || !applePaySession) return undefined

    let cancelled = false
    let activeSession = null

    const parseApplePayScript = async (baseUrl, token) => {
      const scriptUrl = `${String(baseUrl || '').replace(/\/$/, '')}/applepay?data=${encodeURIComponent(token)}`
      const res = await fetch(scriptUrl, { credentials: 'omit' })
      if (!res.ok) throw new Error(`applepay script HTTP ${res.status}`)
      const script = await res.text()
      if (/Forbidden/i.test(script) && !/ApplePaySession/.test(script)) {
        throw new Error('Apple Pay Direct forbidden for this payment type')
      }
      const pick = (re) => {
        const m = script.match(re)
        return m ? m[1] : null
      }
      const validateDomains = buildAppleValidateDomains(baseUrl)
      return {
        merchantIdentifier: pick(/merchantIdentifier\s*=\s*'([^']+)'/) || pick(/'(merchant\.[^']+)'/),
        paymentSessionToken: pick(/token=([0-9]{10,})/),
        checkoutData: pick(/[&?]data=([a-f0-9]{32,})/i) || token,
        sessionId: pick(/session_id=([A-Za-z0-9]+)/),
        paymentType: pick(/paymentType\s*=\s*'([0-9]+)'/) || '13',
        serviceId: pick(/serviceId\s*=\s*'([0-9]+)'/) || '3',
        amount: pick(/checkoutTotal\s*=\s*`([^`]+)`/),
        label: pick(/label:\s*'([^']*)'/) || 'Taste Corner',
        validateUrl: `${String(baseUrl || '').replace(/\/$/, '')}/applepayment`,
        paymentUrl: `${String(baseUrl || '').replace(/\/$/, '')}/payment`,
        validateDomain: validateDomains[0],
        validateDomains,
        directAvailable: true
      }
    }

    const resolveBootstrap = async () => {
      const { bootstrap, token, baseUrl, amount, environment } = applePaySession
      if (bootstrap?.paymentSessionToken && bootstrap?.checkoutData) {
        const fromBoot = Array.isArray(bootstrap.validateDomains) ? bootstrap.validateDomains : []
        const validateDomains = buildAppleValidateDomains(baseUrl, environment, [
          bootstrap.validateDomain,
          ...fromBoot
        ])
        return {
          ...bootstrap,
          validateDomain: validateDomains[0],
          validateDomains,
          amount: bootstrap.amount || amount,
          label: (bootstrap.label && String(bootstrap.label).trim()) || 'Taste Corner'
        }
      }
      return parseApplePayScript(baseUrl, token)
    }

    const startNativeApplePay = async (bootstrap) => {
      if (!window.ApplePaySession) {
        setPayError(t('Payment gateway error'))
        return
      }

      await loadExternalScript(HESABE_APPLE_PAY_SDK)
      if (cancelled) return

      const payId = String(bootstrap.paymentType || '13')
      // KWD requires 3 decimal places for Apple Pay amount strings
      const rawAmount = bootstrap.amount || applePaySession.amount || '0'
      const checkoutTotal = (() => {
        const n = Number(rawAmount)
        return Number.isFinite(n) ? n.toFixed(3) : String(rawAmount)
      })()
      const paymentRequest = {
        currencyCode: 'KWD',
        countryCode: 'KW',
        merchantCapabilities: payId === '11'
          ? ['supports3DS', 'supportsDebit']
          : payId === '12'
            ? ['supports3DS', 'supportsCredit']
            : payId === '13'
              ? ['supports3DS', 'supportsDebit', 'supportsCredit']
              : ['supports3DS'],
        supportedNetworks: ['visa', 'masterCard'],
        total: {
          label: (bootstrap.label && String(bootstrap.label).trim()) || 'Taste Corner',
          type: 'final',
          amount: checkoutTotal
        }
      }
      if (payId === '11' || payId === '12') {
        paymentRequest.supportedCountries = ['KW']
      }

      let paymentNavigating = false
      // Abort leftover session before creating a new one (avoids InvalidAccessError)
      if (activeSession) {
        try { activeSession.abort() } catch (e) { /* ignore */ }
        activeSession = null
      }
      try {
        activeSession = new window.ApplePaySession(3, paymentRequest)
      } catch (createErr) {
        console.log(createErr, 'ApplePaySession create failed')
        setPayError(t('Payment gateway error'))
        return
      }

      activeSession.onvalidatemerchant = async (event) => {
        try {
          // Must validate as the page domain (althogsweet.com), not api.hesabe.com
          const domains = buildAppleValidateDomains(
            applePaySession.baseUrl,
            applePaySession.environment,
            [
              bootstrap.validateDomain,
              ...(Array.isArray(bootstrap.validateDomains) ? bootstrap.validateDomains : [])
            ]
          )
          const validateDomain = domains[0]
          const data = await ApiService('/pay/apple-validate', {
            u: event.validationURL,
            token: bootstrap.paymentSessionToken,
            domain: validateDomain,
            domains,
            serviceId: bootstrap.serviceId || '3',
            payId
          }, 'post')

          const ok = data?.success && (data?.merchantSessionIdentifier || data?.epochTimestamp)
          if (!ok) {
            const msg = data?.statusMessage || data?.message || t('Payment gateway error')
            setPayError(msg)
            try { activeSession.abort() } catch (e) { /* ignore */ }
            activeSession = null
            return
          }

          const pageDomain = getMerchantValidateDomain()
          console.log(
            'Apple Pay validateDomainUsed:',
            data.validateDomainUsed,
            'domainName:',
            data.domainName,
            'page:',
            pageDomain
          )

          // Safari rejects sessions whose domainName ≠ page origin
          if (data.domainName && data.domainName !== pageDomain && data.domainName !== MERCHANT_DOMAIN) {
            setPayError(
              t('Payment gateway error')
              + ` (Apple domain ${data.domainName}, expected ${pageDomain})`
            )
            try { activeSession.abort() } catch (e) { /* ignore */ }
            activeSession = null
            return
          }

          const merchantSession = {
            epochTimestamp: data.epochTimestamp,
            expiresAt: data.expiresAt,
            merchantSessionIdentifier: data.merchantSessionIdentifier,
            nonce: data.nonce,
            merchantIdentifier: data.merchantIdentifier,
            domainName: data.domainName || pageDomain,
            displayName: data.displayName,
            signature: data.signature
          }
          if (data.operationalAnalyticsIdentifier) {
            merchantSession.operationalAnalyticsIdentifier = data.operationalAnalyticsIdentifier
          }
          if (data.pspId) {
            merchantSession.pspId = data.pspId
          }

          activeSession.completeMerchantValidation(merchantSession)
        } catch (err) {
          console.log(err, 'apple merchant validation error')
          setPayError(t('Payment gateway error'))
          try { activeSession.abort() } catch (e) { /* ignore */ }
        }
      }

      activeSession.onpaymentauthorized = (event) => {
        try {
          const paymentToken = event.payment.token
          const action = String(bootstrap.paymentUrl || `${applePaySession.baseUrl}/payment`).replace(/\?.*$/, '')
          const applePaymentToken = paymentToken.paymentData == null
            ? JSON.stringify(paymentToken)
            : (typeof paymentToken.paymentData === 'string'
              ? paymentToken.paymentData
              : JSON.stringify(paymentToken.paymentData))
          const paymentMethod = paymentToken.paymentMethod == null
            ? '{}'
            : (typeof paymentToken.paymentMethod === 'string'
              ? paymentToken.paymentMethod
              : JSON.stringify(paymentToken.paymentMethod))

          // Same pattern as Hesabe /applepay script
          let payUrl = `${action}?token=${encodeURIComponent(bootstrap.paymentSessionToken)}`
            + `&paymentType=${encodeURIComponent(payId)}`
            + `&data=${encodeURIComponent(bootstrap.checkoutData)}`
            + `&applePaymentToken=${encodeURIComponent(applePaymentToken)}`
            + `&paymentMethod=${encodeURIComponent(paymentMethod)}`
            + `&transactionIdentifier=${encodeURIComponent(String(paymentToken.transactionIdentifier || '').toLowerCase())}`
          if (bootstrap.sessionId) {
            payUrl += `&session_id=${encodeURIComponent(bootstrap.sessionId)}`
          }

          paymentNavigating = true
          activeSession.completePayment(window.ApplePaySession.STATUS_SUCCESS)
          window.location.href = payUrl
        } catch (err) {
          console.log(err, 'apple payment authorize error')
          paymentNavigating = false
          try {
            activeSession.completePayment(window.ApplePaySession.STATUS_FAILURE)
          } catch (e) { /* ignore */ }
          setPayError(t('Payment gateway error'))
        }
      }

      activeSession.oncancel = () => {
        activeSession = null
        if (paymentNavigating) return
        setPayError(t('Payment cancelled'))
      }

      activeSession.begin()
    }

    let applePayStarting = false
    const startApplePay = async () => {
      if (applePayStarting) return
      applePayStarting = true
      setPayError('')
      try {
        const bootstrap = await resolveBootstrap()
        if (cancelled) return
        await startNativeApplePay(bootstrap)
      } catch (nativeErr) {
        console.log(nativeErr, 'native apple pay failed')
        setPayError(t('Payment gateway error'))
      } finally {
        applePayStarting = false
      }
    }

    const bind = async () => {
      await new Promise((r) => setTimeout(r, 50))
      if (cancelled) return

      try {
        await loadExternalScript(HESABE_APPLE_PAY_SDK)
      } catch (e) { /* optional until click */ }

      const btn = document.getElementById('applePayment')
      if (!btn) {
        setPayError(t('Payment gateway error'))
        return
      }
      btn.style.display = 'block'
      const onClick = (evt) => {
        evt.preventDefault()
        evt.stopPropagation()
        startApplePay()
      }
      btn.addEventListener('click', onClick)
      return () => btn.removeEventListener('click', onClick)
    }

    let unbindBtn = null
    bind().then((fn) => { unbindBtn = fn })

    return () => {
      cancelled = true
      if (typeof unbindBtn === 'function') unbindBtn()
      try { if (activeSession) activeSession.abort() } catch (e) { /* ignore */ }
      activeSession = null
    }
  }, [applePayReady, applePaySession, t])

  const resetDirectModes = () => {
    setApplePayReady(false)
    setApplePaySession(null)
  }

  const handleSelectMethod = (methodId) => {
    setSelectedMethod(methodId)
    resetDirectModes()
    setPayError('')
  }

  const handlePay = async () => {
    if (!selectedMethod || !payData || paying) return
    if (billMode !== 'payable') return

    if (!isGatewayConfigReady(configs)) {
      setPayError(t('Online payment unavailable'))
      return
    }

    setPaying(true)
    setPayError('')
    resetDirectModes()

    const paymentType = METHOD_TO_APP_PAYMENT_TYPE[selectedMethod] ?? 0
    const responseUrl = getPayResponseUrl(configs)
    const failureUrl = getPayFailureUrl(configs)
    const merchantDomain = (typeof window !== 'undefined' && window.location?.hostname)
      ? window.location.hostname.replace(/^www\./, '')
      : MERCHANT_DOMAIN

    const buildPaymentObj = (opts = {}) => ({
      orderReferenceNumber: payData.reference,
      amount: payData.amount,
      paymentType: opts.paymentType ?? paymentType,
      currency: configs.currency || 'KWD',
      responseUrl,
      failureUrl,
      variable1: payData.orderNo || payData.reference,
      variable2: opts.variable2 ?? selectedMethod,
      variable3: payData.date,
      variable4: '',
      // Hesabe Direct Apple Pay requires merchant domain in variable5
      variable5: selectedMethod === 'applepay' ? merchantDomain : '',
      googlePay: false,
      applePay: opts.applePay === true
    })

    try {
      const result = await ApiService('/pay/checkout', buildPaymentObj({
        applePay: selectedMethod === 'applepay'
      }), 'post')

      if (!result?.success) {
        const gatewayMsg = typeof result?.message === 'string' && result.message.trim()
          ? result.message.trim()
          : null
        setPayError(gatewayMsg || t('Payment gateway error'))
        setPaying(false)
        return
      }

      try {
        sessionStorage.setItem('payLinkReference', String(payData.reference))
      } catch (e) { /* ignore */ }

      const { url, token, baseUrl, environment } = parseCheckoutResult(result)

      // Direct Apple Pay — always stay on-site (native Apple Pay button / sheet)
      if (selectedMethod === 'applepay') {
        if (!token && !url) {
          setPayError(t('Payment gateway error'))
          setPaying(false)
          return
        }
        // Prefer page domain (althogsweet.com); Hesabe api host is fallback only
        const fromApi = (result.applePay && Array.isArray(result.applePay.validateDomains))
          ? result.applePay.validateDomains
          : []
        const validateDomains = buildAppleValidateDomains(baseUrl, environment, [
          result.applePay && result.applePay.validateDomain,
          result.applePay && result.applePay.merchantDomain,
          ...fromApi
        ])
        const validateDomain = validateDomains[0]
        const bootstrap = result.applePay
          ? {
            ...result.applePay,
            validateDomain,
            validateDomains,
            label: (result.applePay.label && String(result.applePay.label).trim()) || 'Taste Corner'
          }
          : {
            validateDomain,
            validateDomains
          }

        setApplePaySession({
          token,
          baseUrl,
          url,
          environment,
          locale: isEnglish ? 'en' : 'ar',
          reference: payData.reference,
          amount: payData.amount,
          bootstrap: {
            ...(result.applePay || {}),
            ...bootstrap,
            checkoutData: (result.applePay && result.applePay.checkoutData) || token,
            paymentUrl: (result.applePay && result.applePay.paymentUrl)
              || `${String(baseUrl || '').replace(/\/$/, '')}/payment`,
            validateUrl: (result.applePay && result.applePay.validateUrl)
              || `${String(baseUrl || '').replace(/\/$/, '')}/applepayment`,
            amount: (result.applePay && result.applePay.amount) || payData.amount,
            // Prefer checkout paymentType (13); Hesabe script may still echo another id
            paymentType: String(paymentType)
              || (result.applePay && result.applePay.paymentType)
              || '13',
            serviceId: (result.applePay && result.applePay.serviceId) || '3'
          }
        })
        setApplePayReady(true)
        setPaying(false)
        return
      }

      // Google Pay — always redirect to Hesabe hosted checkout
      if (selectedMethod === 'googlepay') {
        if (url) {
          window.location.href = url
          return
        }
        setPayError(t('Payment gateway error'))
        setPaying(false)
        return
      }

      // Credit card — full-page Hesabe checkout (not iframe).
      // MPGS/NBK 3DS sets X-Frame-Options: sameorigin and refuses nested frames
      // (test-nbkpayment.mtf.gateway.mastercard.com / production ACS).
      if (selectedMethod === 'credit') {
        if (url) {
          window.location.href = url
          return
        }
        setPayError(t('Payment gateway error'))
        setPaying(false)
        return
      }

      if (url) {
        window.location.href = url
        return
      }
      setPayError(t('Payment gateway error'))
      setPaying(false)
    } catch (err) {
      console.log(err, 'pay/checkout error')
      setPayError(t('Payment gateway error'))
      setPaying(false)
    }
  }

  const langToggle = (
    <LangBar>
      <LangBtn type='button' onClick={toggleLanguage} aria-label='Switch language'>
        {currentLng === 'en' ? 'ع' : 'EN'}
      </LangBtn>
    </LangBar>
  )

  if (loadingPay) {
    return (
      <PayPage>
        <BillCard>
          {langToggle}
          <ErrorCard style={{ color: '#6b5b4b', boxShadow: 'none', margin: 0 }}>
            {t('Please wait')}
          </ErrorCard>
        </BillCard>
      </PayPage>
    )
  }

  if (!payData) {
    return (
      <PayPage>
        <BillCard>
          {langToggle}
          <ErrorCard style={{ boxShadow: 'none', margin: 0 }}>
            {payError || t('Missing payment reference')}
          </ErrorCard>
        </BillCard>
      </PayPage>
    )
  }

  const raw = payData.raw
  const showMethods = billMode === 'payable'
  const showStatus = billMode === 'success' || billMode === 'failed'
  const statusSuccess = billMode === 'success'

  return (
    <PayPage>
      <BillCard>
        {langToggle}
        <BillHeader>
          {logoSrc ? (
            <LogoCircle>
              <img src={logoSrc} alt='logo' />
            </LogoCircle>
          ) : (
            <LogoPlaceholder />
          )}
          <BillTitle>
            {t('Pay Order')} {payData.reference}
          </BillTitle>
        </BillHeader>

        {billMode === 'expired' && (
          <StatusBanner success={false}>
            <BsXCircle />
            <div className='title'>{t('Payment link expired')}</div>
            <div className='hint'>{t('Payment link expired hint')}</div>
          </StatusBanner>
        )}

        {showStatus && (
          <StatusBanner success={statusSuccess}>
            {statusSuccess ? <BsCheckCircle /> : <BsXCircle />}
            <div className='title'>
              {statusSuccess ? t('Payment Successful') : t('Payment Failed')}
            </div>
            {raw?.message && <div className='hint'>{raw.message}</div>}
          </StatusBanner>
        )}

        <AmountBlock>
          <div className='label'>
            {showStatus ? t('Amount') : t('Amount Due')}
          </div>
          <div className='value'>
            <span>{payData.amount}</span>
            <span className='currency'>{t(currency)}</span>
          </div>
        </AmountBlock>

        <BillBody>
          <DetailRow>
            <DetailTitle>{t('Reference No')}</DetailTitle>
            <DetailVal>{payData.reference}</DetailVal>
          </DetailRow>
          <DetailRow>
            <DetailTitle>{t('Order No.')}</DetailTitle>
            <DetailVal>{payData.orderNo || '—'}</DetailVal>
          </DetailRow>
          <DetailRow>
            <DetailTitle>{t('Date')}</DetailTitle>
            <DetailVal>{payData.date}</DetailVal>
          </DetailRow>

          {showStatus && (
            <>
              <DetailRow>
                <DetailTitle>{t('Status')}</DetailTitle>
                <StatusVal success={statusSuccess}>
                  {statusSuccess ? t('Success') : t('Failed')}
                </StatusVal>
              </DetailRow>
              {raw?.resultCode && (
                <DetailRow>
                  <DetailTitle>{t('Result Code')}</DetailTitle>
                  <DetailVal>{raw.resultCode}</DetailVal>
                </DetailRow>
              )}
              {raw?.paymentId && (
                <DetailRow>
                  <DetailTitle>{t('Payment Id')}</DetailTitle>
                  <DetailVal>{raw.paymentId}</DetailVal>
                </DetailRow>
              )}
              {raw?.transactionId && (
                <DetailRow>
                  <DetailTitle>{t('Bank Transaction Id')}</DetailTitle>
                  <DetailVal>{raw.transactionId}</DetailVal>
                </DetailRow>
              )}
              {(raw?.variable2 || raw?.method) && (
                <DetailRow>
                  <DetailTitle>{t('Payment Method')}</DetailTitle>
                  <DetailVal>{raw.variable2 || raw.method}</DetailVal>
                </DetailRow>
              )}
            </>
          )}
        </BillBody>

        {showMethods && (
          <MethodsSection>
            <MethodsTitle>{t('Select Payment Method')}</MethodsTitle>
            <MethodsList>
              {PAYMENT_METHODS.map((method) => {
                const active = selectedMethod === method.id
                const isBrandMark = method.id === 'applepay' || method.id === 'googlepay'
                return (
                  <MethodItem
                    key={method.id}
                    type='button'
                    active={active}
                    onClick={() => handleSelectMethod(method.id)}
                  >
                    <MethodIcons $brand={isBrandMark}>
                      {(method.imageIcons || []).map((iconKey) => (
                        <img
                          key={iconKey}
                          src={theme?.images?.payment?.[iconKey]}
                          alt={iconKey}
                        />
                      ))}
                    </MethodIcons>
                    <MethodName>{t(method.labelKey)}</MethodName>
                  </MethodItem>
                )
              })}
            </MethodsList>
          </MethodsSection>
        )}

        {payError && (
          <ErrorCard style={{ margin: '8px 16px', boxShadow: 'none' }}>
            {payError}
          </ErrorCard>
        )}

        <BillFooter>
          {showMethods && applePayReady && selectedMethod === 'applepay' ? (
            <ApplePayWrap>
              {/* Hesabe Direct Apple Pay binds #applePayment and .applePaybtn */}
              <button
                id='applePayment'
                type='button'
                className='apple-pay-button applePaybtn'
                lang={isEnglish ? 'en' : 'ar'}
                aria-label='Apple Pay'
              />
              <div className='hint'>{t('Apple Pay ready hint')}</div>
            </ApplePayWrap>
          ) : showMethods ? (
            <Button
              color='primary'
              width='100%'
              disabled={!selectedMethod || paying}
              onClick={handlePay}
            >
              {paying ? t('Please wait') : t('Pay Now')}
            </Button>
          ) : null}
          {showStatus && (
            <Button
              color='primary'
              width='100%'
              onClick={() => history.push(`/transaction/${payData.reference}`)}
            >
              {t('View Transaction')}
            </Button>
          )}
        </BillFooter>
      </BillCard>

      <CompanyCard>
        {companyName && <CompanyName>{companyName}</CompanyName>}
        <CompanyLine>
          {t(isEnglish ? 'Copyright Footer EN' : 'Copyright Footer AR')}
        </CompanyLine>
        <CompanyLine>
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          {whatsAppUrl && (
            <>
              {' · '}
              <a href={whatsAppUrl} target='_blank' rel='noopener noreferrer'>
                WhatsApp
              </a>
            </>
          )}
        </CompanyLine>
      </CompanyCard>
    </PayPage>
  )
}
